import getSignUpModalProductImage, {
  QueryParameters as autoSignupModalQueryParams,
} from "@faire/web-api/api/seo/sign-up-modal/get";
import checkUserAvailability from "@faire/web-api/api/user/availability/post";
import userLoginData from "@faire/web-api/api/user/login-data/post";
import saveIncompleteSignupAttempt from "@faire/web-api/api/user/retailer-signup-attempt/post";
import { trackFaireDirectShoppableLandingPageModalCloseClick } from "@faire/web-api/events/faireDirect/click/shoppableLandingPageModalClose";
import { trackSignupReportExistingEmail } from "@faire/web-api/events/signup/actionUnknown/reportExistingEmail";
import { trackSignupReportExistingSso } from "@faire/web-api/events/signup/actionUnknown/reportExistingSso";
import { trackSignupCloseSignUpModalClick } from "@faire/web-api/events/signup/click/closeSignUpModal";
import { trackSignupTimeFromEmailToButtonTiming } from "@faire/web-api/events/signup/timing/timeFromEmailToButton";
import { ICheckEmailAddressAndBusinessAvailabilityRequest } from "@faire/web-api/indigofair/data/ICheckEmailAddressAndBusinessAvailabilityRequest";
import { IGetBestImageResponse } from "@faire/web-api/indigofair/data/IGetBestImageResponse";
import { IImage } from "@faire/web-api/indigofair/data/IImage";
import { ILoginDataRequest } from "@faire/web-api/indigofair/data/ILoginDataRequest";
import { ISignUpRequest } from "@faire/web-api/indigofair/data/ISignUpRequest";
import { IUser } from "@faire/web-api/indigofair/data/IUser";
import { SocialSignOnProvider } from "@faire/web-api/indigofair/data/SocialSignOnProvider";
import { ISignUpModalInfo } from "@faire/web-api/indigofair/seo/ISignUpModalInfo";
import { route as openWithFaireRoute } from "@faire/web-api/routes/open-with-faire";
import { route as videoPageRoute } from "@faire/web-api/routes/video/videoToken";
import signUpPersonal, {
  QueryParameters as SignUpQueryParameters,
} from "@faire/web-api/routes/welcome/r/personal";
import { getWebApiErrorMessage } from "@faire/web/common/getErrorMessage";
import { StrictLocalizeFunction } from "@faire/web/common/localization";
import { makeObservable } from "@faire/web/common/makeObservable";
import { QueryParams } from "@faire/web/common/query-params/QueryParams";
import { FAIRE_DIRECT_BRAND_QUERY_PARAM } from "@faire/web/common/routes/faire-direct/FAIRE_DIRECT_BRAND_QUERY_PARAM";
import { pathMatchesRoute } from "@faire/web/common/routes/RouteMatcher";
import { Storage } from "@faire/web/common/Storage";
import { TimingMarker } from "@faire/web/common/timings/TimingMarker";
import { trackEvent } from "@faire/web/common/trackEvent";
import { removeLocaleFromUrl } from "@faire/web/ui/routing/util";
import { History, Location } from "history";
import { computed, observable } from "mobx";
import { fromPromise, IPromiseBasedObservable } from "mobx-utils";
import qs from "qs";
import * as React from "react";

import { recordSignupActivity } from "@faire/retailer/actions/user/recordLoggedOutActivity";
import { SAMPLE_PROSPECTING_POST_SAMPLE_QUERY_PARAM_VALUE } from "@faire/retailer/components/App/AppModals/utils";
import {
  AppSignUpModalVariant,
  determineAppSignUpModalVariant,
  isAutoSignUpVariant,
  isFaireDirectVariant,
} from "@faire/retailer/components/AppSignUp/AppSignUpModalVariant";
import {
  notifySignupSnackbarEvent,
  SignUpSnackbarEvent,
} from "@faire/retailer/components/LoggedOutMobileSignUpSnackbar/useSignupSnackbarStateMachine";
import { FormField } from "@faire/retailer/components/SignUpForm/SignUpFormState";
import {
  isFaireEmail,
  isInvalidEmail,
} from "@faire/retailer/components/SignUpForm/validate";
import { SignUpType } from "@faire/retailer/lib/popSignUp";
import { showSSOButtons } from "@faire/retailer/lib/ssoUtils";
import { ElevatePortalStore } from "@faire/retailer/stores/domain/ElevatePortalStore";
import { ProductCacheStore } from "@faire/retailer/stores/domain/ProductCacheStore";
import { SSOStore } from "@faire/retailer/stores/domain/SSOStore";

import { AppSignUpQueryParams, getSignUpParams } from "./AppSignUpQueryParams";

export const HAS_CLOSED_SIGNUP_MODAL = "has-closed-signup-modal";

export class AppSignUpState {
  @observable
  email: FormField<string>;

  @observable
  storeOpeningDate: FormField<number | undefined>;

  @observable
  private productBestImageResponse?: IPromiseBasedObservable<IGetBestImageResponse>;

  @observable
  private signUpModalProductImageResponse?: IPromiseBasedObservable<ISignUpModalInfo>;

  private timingMarkerUserEntersEmail: TimingMarker | undefined;

  readonly modalVariant: AppSignUpModalVariant;
  readonly signUpParams: AppSignUpQueryParams;

  constructor(
    private readonly queryParams: QueryParams,
    private readonly location: Location,
    private strictLocalize: StrictLocalizeFunction,
    private history: History
  ) {
    makeObservable(this);

    this.signUpParams = getSignUpParams(queryParams);
    this.email = new FormField(
      this.signUpParams.email ?? "",
      "email",
      isInvalidEmail,
      "sign-up-page/modal"
    );

    this.storeOpeningDate = new FormField<number | undefined>(
      undefined,
      "store-opening-date",
      () => false,
      "sign-up-page/modal"
    );

    this.modalVariant = determineAppSignUpModalVariant(queryParams, location);
  }

  @computed
  get isOpenWithFaireSignUp(): boolean {
    return pathMatchesRoute(this.location, openWithFaireRoute);
  }

  get productBestImage(): IImage | undefined {
    return this.productBestImageResponse?.case({
      fulfilled: (r) => r?.image,
    });
  }

  get signUpModalProductImage(): IImage | undefined {
    return this.signUpModalProductImageResponse?.case({
      fulfilled: (r) => r?.image,
    });
  }

  @computed
  get dismissible(): boolean {
    return (
      !this.signUpParams.livestream &&
      !pathMatchesRoute(this.location, videoPageRoute)
    );
  }

  @computed
  get isFeaturedImageLoading(): boolean {
    return (
      this.signUpModalProductImageResponse?.state === "pending" ||
      this.productBestImageResponse?.state === "pending"
    );
  }

  handleSubmit = async (
    e?: React.FormEvent,
    queryParamsOverride?: Partial<SignUpQueryParameters>
  ) => {
    e?.preventDefault();
    e?.stopPropagation();

    trackEvent(`sign-up-page/modal/submit-email/attempt`);
    this.trackTimeBetweenEmailEntryAndSubmit();
    if (this.setEmailValidationErrors()) {
      trackEvent(`sign-up-page/modal/submit-email/invalid`);
      return;
    }

    try {
      const trimmedEmailAddress = this.email.value.trim();
      const { email_address_available } = await checkUserAvailability(
        ICheckEmailAddressAndBusinessAvailabilityRequest.build({
          email_address: trimmedEmailAddress,
        })
      );
      if (email_address_available) {
        if (isFaireEmail(this.email.value)) {
          const emailError = this.strictLocalize({
            defaultMessage:
              "Use your Faire email address with a + segment (e.g. your.email+test@faire.com)",
            description: {
              text: "Error message shown to user when trying to create an account with a faire email.",
              img: "https://cdn.faire.com/fastly/6318e502de81a22dc4623061cc0d6539d5af1817a27d8a636c60ed89add393b0.png",
            },
          });
          this.email.setError(emailError);
          return;
        }
        // we want to show the widget in sign-up flow if it's coming from a sample prospecting:
        const signUpType =
          this.signUpParams.signUp ===
          SAMPLE_PROSPECTING_POST_SAMPLE_QUERY_PARAM_VALUE
            ? SignUpType.WIDGET_V2
            : SignUpType.FAIRE_DIRECT;
        const faireDirectParams = isFaireDirectVariant(this.modalVariant)
          ? {
              signUp: signUpType,
              brand: ElevatePortalStore.get().elevateBrandToken,
              brandName: ElevatePortalStore.get().elevateBrand?.name,
              [FAIRE_DIRECT_BRAND_QUERY_PARAM]:
                ElevatePortalStore.get().faireDirectAlias,
            }
          : /**
             * We do not want the `fdb` query param to be passed into the sign-up flow
             * if the modal is not a Faire Direct variant in order to prevent incorrectly
             * giving the Faire Direct credit.
             */
            {
              [FAIRE_DIRECT_BRAND_QUERY_PARAM]: undefined,
            };
        // shouldn't be passing a store opening date to BE if its less than or 0
        const storeOpeningDate =
          this.storeOpeningDate.value && this.storeOpeningDate.value > 0
            ? `${this.storeOpeningDate.value}`
            : undefined;
        const query = {
          ...this.queryParams,
          email: trimmedEmailAddress,
          source: removeLocaleFromUrl(this.location.pathname),
          storeOpeningDate,
          ...faireDirectParams,
          ...queryParamsOverride,
        };

        // For leveraging logged out signals from product signups
        if (this.modalVariant === AppSignUpModalVariant.PRODUCT) {
          recordSignupActivity(
            {
              product_token: this.signUpParams.product ?? undefined,
              brand_token: this.signUpParams.brand ?? undefined,
            },
            this.location.pathname
          );
        } else if (isFaireDirectVariant(this.modalVariant)) {
          recordSignupActivity(
            {
              brand_token:
                ElevatePortalStore.get().elevateBrandToken ?? undefined,
            },
            this.location.pathname
          );
        }
        const redirectLocation: string = signUpPersonal(query);

        trackEvent(`sign-up-page/modal/submit-email/success`);
        this.history.push(redirectLocation);
        await this.saveIncompleteSignup();
      } else {
        const { available_social_providers } = await userLoginData(
          ILoginDataRequest.build({ email_address: this.email.value })
        );
        if ((available_social_providers?.length ?? 0) > 0 && showSSOButtons()) {
          SSOStore.get().setProviders(
            available_social_providers.map(
              (provider) => SocialSignOnProvider[provider]
            )
          );
          SSOStore.get().setPromptSSO(true);
          trackSignupReportExistingSso();
        } else {
          trackSignupReportExistingEmail();
        }
        trackEvent(`sign-up-page/modal/submit-email/in-use`);
        this.history.replace({
          ...this.history.location,
          search: qs.stringify({
            ...this.queryParams,
            signUp: undefined,
            signIn: this.signUpParams.signUp ?? "1",
            email: trimmedEmailAddress,
          }),
        });
      }
    } catch (error) {
      const defaultMessage = this.strictLocalize({
        defaultMessage: "Something went wrong. Please try again later.",
        description: {
          text: "This error states that an unexpected error occurred",
        },
      });
      this.email.setError(
        getWebApiErrorMessage(error, this.strictLocalize, defaultMessage)
      );
    }
  };

  private saveIncompleteSignup = async () => {
    const payload = ISignUpRequest.build({
      user: IUser.build({
        email_address: this.email.value,
      }),
    });
    try {
      await saveIncompleteSignupAttempt(payload);
    } catch (error) {
      // error handling be BE; background network task, user shouldn't know
      // this request failed
    }
  };

  handleModalOnClose = () => {
    this.history.replace({
      ...this.history.location,
      search: qs.stringify({
        ...this.queryParams,
        signUp: undefined,
        product: undefined,
        brand: undefined,
        video: undefined,
        source: undefined,
        email: undefined,
        refB: undefined,
        refC: undefined,
        refF: undefined,
        refS: undefined,
        refOrigin: undefined,
        faireMarket: undefined,
      }),
    });

    if (isFaireDirectVariant(this.modalVariant)) {
      trackFaireDirectShoppableLandingPageModalCloseClick();
    } else {
      trackSignupCloseSignUpModalClick();
    }

    if (isAutoSignUpVariant(this.modalVariant)) {
      notifySignupSnackbarEvent(SignUpSnackbarEvent.AUTO_SIGNUP_CLOSED);
    }

    Storage.session.setItem(HAS_CLOSED_SIGNUP_MODAL, "true");
  };

  fetchProductBestImage = () => {
    const productToken =
      this.modalVariant === AppSignUpModalVariant.FACEBOOK
        ? this.queryParams.getOrDefault("refP", "")
        : this.queryParams.getOrDefault("product", "");
    this.productBestImageResponse = fromPromise(
      ProductCacheStore.get().getProductBestImage(productToken)
    );
  };

  fetchSignUpModalProductImage = () => {
    this.signUpModalProductImageResponse = fromPromise(
      getSignUpModalProductImage(autoSignupModalQueryParams.build({}))
    );
  };

  setEmailValidationErrors = (): boolean => {
    if (this.email.isInvalid) {
      const emailError = this.strictLocalize({
        defaultMessage: "Please enter a valid email address",
        description: {
          text: "Error message shown to user when invalid email address is entered",
        },
      });
      this.email.setError(emailError);
      return true;
    }

    return false;
  };

  markUserEntersEmail = () => {
    if (!this.timingMarkerUserEntersEmail) {
      this.timingMarkerUserEntersEmail = new TimingMarker();
    }
  };

  private trackTimeBetweenEmailEntryAndSubmit = () => {
    const end = new TimingMarker();
    if (this.timingMarkerUserEntersEmail) {
      trackSignupTimeFromEmailToButtonTiming({
        start: this.timingMarkerUserEntersEmail,
        end,
      });
    }
  };
}
